import React from 'react';
import {
  DistinctIdLink, formatDuration, formatNumber, formatCost, formatDate,
  formatRelativeTime, truncate, StatusDot, LevelBadge, StatusCodePill,
} from 'features/agent-analytics/components/runCells';
import { KindPill } from 'features/agent-analytics/components/spanKind';
import AnnotationBadge from 'features/agent-analytics/annotations/AnnotationBadge';

/** Click-through to the parent run's drawer. When a parent component passes
 *  `onRunClick`, the cell becomes a button that opens the AgentRunDetailPage
 *  overlay in place. Without that callback (e.g. tests or stand-alone use),
 *  it falls back to plain text — never blocks rendering. */
export function RunIdLink({ value, onRunClick, compact = true }) {
  if (value == null || value === '') {
    return (
      <span
        title="Runless span: written without a parent agent run (e.g. via track_mcp)."
        className="rounded bg-[color:var(--surface-hover)] px-1.5 py-0.5 font-mono text-[10px] text-[color:var(--text-muted)]"
      >
        runless
      </span>
    );
  }
  const label = String(value);
  const display = compact
    ? `${label.slice(0, 8)}…`
    : (label.length > 14 ? `${label.slice(0, 14)}…` : label);
  if (typeof onRunClick !== 'function') {
    return <span className="font-mono text-[11px] text-[color:var(--text-secondary)]" title={label}>{display}</span>;
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onRunClick(label); }}
      title={`Open run ${label}`}
      className="font-mono text-[11px] text-[color:var(--text-primary)] underline-offset-2 hover:underline"
    >
      {display}
    </button>
  );
}

/** Click-through to a parent span's drawer. Same callback pattern as
 *  RunIdLink — falls back to plain text when no callback is supplied. */
export function ParentSpanLink({ value, onSpanClick }) {
  if (value == null || value === '') return <span className="text-[color:var(--text-muted)]">—</span>;
  const label = String(value);
  const truncated = `${label.slice(0, 8)}…`;
  if (typeof onSpanClick !== 'function') {
    return <span className="font-mono text-[11px] text-[color:var(--text-secondary)]" title={label}>{truncated}</span>;
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onSpanClick(label); }}
      title={`Open parent span ${label}`}
      className="font-mono text-[11px] text-[color:var(--text-primary)] underline-offset-2 hover:underline"
    >
      {truncated}
    </button>
  );
}

/** Span kind chip — small uppercase tag with a colored accent.
 *  Lives in spanKind.js now: the run drawer carried a SECOND copy of this map
 *  that disagreed with it (tool amber here, blue there), so the same span
 *  changed colour between the table and the drawer opened from it. Re-exported
 *  so existing importers of spanCells keep working.
 */
export { KindPill };

/** Render one cell of the spans table. Optional `callbacks` lets the parent
 *  inject onRunClick / onSpanClick so the run / parent-span pills become
 *  active overlays instead of dead text. */
export function renderSpanCell(key, row, callbacks = {}) {
  const { onRunClick, onSpanClick, onAnnotationClick } = callbacks;
  switch (key) {
    case 'span_id':
      // Compound leading cell: status dot · kind tag · truncated span id.
      // Mirrors the runs table where Run ID carries the status indicator.
      return (
        <span
          className="inline-flex items-center gap-2"
          title={`${row.status || ''} ${row.kind || ''} ${row.span_id || ''}`.trim()}
        >
          <StatusDot status={row.status} />
          <KindPill kind={row.kind} />
          <span className="font-mono text-[11px] text-[color:var(--text-secondary)]">
            {truncate(row.span_id, 14)}
          </span>
          <AnnotationBadge
            spanId={row.span_id}
            spanStartedAt={row.started_at}
            onClick={typeof onAnnotationClick === 'function'
              ? (e) => { e.stopPropagation(); onAnnotationClick(row); }
              : undefined}
          />
        </span>
      );
    // Kept for back-compat in case someone still references it (drawer header etc.)
    case 'kind':
      return <KindPill kind={row.kind} />;
    case 'name':
      return (
        <span className="font-medium text-[color:var(--text-primary)]" title={row.name}>
          {truncate(row.name, 48)}
        </span>
      );
    case 'agent_name':
      return row.agent_name
        ? <span className="text-[color:var(--text-primary)]">{row.agent_name}</span>
        : <span className="text-[color:var(--text-muted)]">—</span>;
    case 'started_at':
      return (
        <span className="tabular-nums text-[color:var(--text-secondary)]" title={formatDate(row.started_at)}>
          {formatRelativeTime(row.started_at)}
        </span>
      );
    case 'ended_at':
      return (
        <span className="tabular-nums text-[color:var(--text-secondary)]" title={formatDate(row.ended_at)}>
          {formatRelativeTime(row.ended_at)}
        </span>
      );
    case 'duration_ms':
      return <span className="tabular-nums">{formatDuration(row.duration_ms)}</span>;
    case 'temperature':
      return row.temperature != null
        ? <span className="tabular-nums">{Number(row.temperature).toFixed(2)}</span>
        : <span className="text-[color:var(--text-muted)]">—</span>;
    case 'distinct_id':
      return <DistinctIdLink value={row.distinct_id} />;
    case 'tool_name':
      return row.tool_name
        ? <span className="font-mono text-[11px] text-[color:var(--text-secondary)]">{truncate(row.tool_name, 32)}</span>
        : <span className="text-[color:var(--text-muted)]">—</span>;
    case 'model':
      return row.model
        ? <span className="font-mono text-[11px] text-[color:var(--text-secondary)]">{truncate(row.model, 32)}</span>
        : <span className="text-[color:var(--text-muted)]">—</span>;
    case 'provider':
      return row.provider
        ? <span className="text-[11px] text-[color:var(--text-secondary)]">{row.provider}</span>
        : <span className="text-[color:var(--text-muted)]">—</span>;
    case 'input_tokens':
      return <span className="tabular-nums">{formatNumber(row.input_tokens)}</span>;
    case 'output_tokens':
      return <span className="tabular-nums">{formatNumber(row.output_tokens)}</span>;
    case 'cost':
      return <span className="tabular-nums">{formatCost(row.cost)}</span>;
    case 'run_id':
      return <RunIdLink value={row.run_id} onRunClick={onRunClick} />;
    case 'conversation_id':
      return <span className="font-mono text-[11px] text-[color:var(--text-secondary)]" title={row.conversation_id}>
        {truncate(row.conversation_id, 16)}
      </span>;
    case 'error': {
      const hasError = row.status === 'error' || row.error_type || row.error_message || row.status_code;
      if (!hasError) return <span className="text-[color:var(--text-muted)]">—</span>;
      const title = [row.error_type, row.status_code && `status ${row.status_code}`, row.error_message]
        .filter(Boolean).join(' · ');
      return (
        <span className="inline-flex items-center gap-1.5" title={title}>
          {row.error_type
            ? <span className="font-mono text-[11px] text-[color:var(--feedback-error)]">{truncate(row.error_type, 28)}</span>
            : <span className="text-[11px] text-[color:var(--feedback-error)]">error</span>}
          <StatusCodePill code={row.status_code} />
          <LevelBadge level={row.level} status="error" />
        </span>
      );
    }
    case 'parent_span_id':
      return <ParentSpanLink value={row.parent_span_id} onSpanClick={onSpanClick} />;
    default:
      return <span className="text-[color:var(--text-muted)]">—</span>;
  }
}
